import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import Content from '../layout/content/Content'
import Head from '../layout/head/Head'
import { useTranslation } from 'react-i18next'
import {
  ImporterMapGraphSection,
  ImporterTableSection,
  PotentialMapGraphSection,
  PotentialTableSection
} from './mc-components/McPageComponents'
import FirmDetailsModal from '../components/firm-card/FirmDetailsModal'
import McPageHelpers from './mc-components/McPageHelpers'
import {
  useDSimporterGraphMapSectionExplanation,
  useDSpotentialGraphMapSectionDataExplanation,
  useDStotalImporterCompanyCount,
  useDStotalPotentialCompanyCount,
  useDSrawPotentialMap,
  useDSrawImporterMap,
  useDSselectedTableName,
  useDStradeRegistryTables,
  useDataStoreActions,
  useDStradeRegistryDropdownSelection
} from '../store/dataStore'
import McPageHeader from '../components/page-header/McPageHeader'
import McPdfExport from './mc-components/McPdfExport'
import { useSDSsearchParams } from '../store/searchDataStore'
import { usePDFselectedComponentIds } from '../store/pdfStore'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import classnames from 'classnames'
import { MarketCalculusCard } from '../components/preview/Preview'
import TradeRegistryTabs from './mc-components/importer-companies/TradeRegistryTabs'
import { useRAWtradeRegistry } from '../store/rawStore'
import { Tooltip } from '@mui/material'
import { useGlobalStoreData } from '../store/globalStore'

const ImporterCompanies = forwardRef((props, ref) => {
  const { t } = useTranslation()
  const searchParams = useSDSsearchParams()
  const selectedComponentIds = usePDFselectedComponentIds()

  const importerGraphMapSectionExplanation = useDSimporterGraphMapSectionExplanation()
  const potentialGraphMapSectionDataExplanation = useDSpotentialGraphMapSectionDataExplanation()
  const totalImporterCompanyCount = useDStotalImporterCompanyCount()
  const totalPotentialCompanyCount = useDStotalPotentialCompanyCount()
  const rawPotentialMap = useDSrawPotentialMap()
  const rawImporterMap = useDSrawImporterMap()
  const selectedTableName = useDSselectedTableName()
  const tradeRegistryTables = useDStradeRegistryTables()
  const tradeRegistryDropdownSelection = useDStradeRegistryDropdownSelection()
  const { setTradeRegistryTables, setTradeRegistryDropdownSelection } = useDataStoreActions()
  const languageGSD = useGlobalStoreData.language()

  const header = `${t('graph_importer_analysis')} (${totalImporterCompanyCount} ${t('piece')})`
  const headerPotential = `${t('graph_potential_analysis')} (${totalPotentialCompanyCount} ${t('piece')})`
  const importerCountryName = McPageHelpers.findCountryName(searchParams.countryToExport)
  const subTitle = `${importerCountryName} ${searchParams.hsCode}`
  const subPotTitle = `${t('world')} ${searchParams.hsCode}`
  const searchHeader = `${searchParams.hsCode} - ${importerCountryName} - ${McPageHelpers.findCountryName(searchParams.yourCountry)}`

  useEffect(() => {
    const params = new URL(window.location.href).searchParams
    if (params.has('scroll')) {
      McPageHelpers.scrollToElement(params.get('scroll'))
    }
  }, [])

  const importerTableRef = useRef(null)
  const potentialTableRef = useRef(null)

  const [activeTab, setActiveTab] = useState(1)
  const toggleAltTab = altTab => activeTab !== altTab && setActiveTab(altTab)

  const tradeRegistry = useRAWtradeRegistry()
  const dynamicTabs = useMemo(() => Object.keys(tradeRegistry || {}), [tradeRegistry])

  const columnsForPdf = [
    {
      name: (<div style={{ textAlign: 'center' }}>{t('mailTemplate.wizard.firmList.firmName')}</div>),
      selector: row => row.companyName,
      wrap: true,
      left: true,
      width: '20%'
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t('mailTemplate.wizard.firmList.website')}</div>),
      selector: row => row.website,
      wrap: true,
      left: true,
      width: '15%',
      cell: row => (row.website)
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t('mailTemplate.wizard.firmList.email')}</div>),
      selector: row => row.emailList?.[0],
      wrap: true,
      left: true,
      width: '20%',
      cell: row => (
        <>
          {row.emailList?.length > 1
            ? (
              <Tooltip
                title={
                  <div style={{ whiteSpace: 'pre-line' }}>
                    {row.emailList.slice(1).join('\n')}
                  </div>
                }
                arrow
              >
                <span>{row.emailList?.[0]}...</span>
              </Tooltip>
              )
            : row.emailList?.[0]}
        </>
      )
    },
    {
      name: (<div style={{ textAlign: 'center' }}>{t('mailTemplate.wizard.firmList.sector')}</div>),
      selector: row => row.companySectors?.map(sector => `-${sector.code}-${sector?.name?.[languageGSD] || ''}`).join(', '),
      wrap: true,
      left: true,
      width: '25%',
      cell: row => (
        <Tooltip
          title={
            <div style={{ whiteSpace: 'pre-line' }}>
              {row.companySectors.map(sector => `-${sector.code}-${sector?.name?.[languageGSD] || ''}`).join('\n')}
            </div>
          }
          arrow
        >
          <span>{`${row.companySectors?.[0]?.code}${row.companySectors?.length > 1 ? '...' : ''}`}</span>
        </Tooltip>
      )
    }
  ]

  const pdfDownload = (onlyContent = false) => {
    const importerTablePdfContent = importerTableRef.current.generateImporterTablePdf('importerTablePdfContent')
    const potentialTablePdfContent = potentialTableRef.current.generateImporterTablePdf('potentialTablePdfContent')

    const tradeRegistryTablesPdfContent = dynamicTabs.map(tableId => {
      if (selectedComponentIds.includes(tableId)) {
        const result = {
          [tableId]: [
            McPageHelpers.generateDataTableContentForPdf(columnsForPdf, tradeRegistryTables[tableId], true, 'after')
          ]
        }
        return result
      } else return null
    })

    const filterImporterMap = rawImporterMap?.filter(item => item.formatValue)
    const filterPotentialMap = rawPotentialMap?.filter(item => item.formatValue || item.avgPrice)

    return McPdfExport.ImporterCompanies(
      onlyContent,
      searchHeader,
      filterImporterMap,
      filterPotentialMap,
      importerTablePdfContent,
      potentialTablePdfContent,
      tradeRegistryTablesPdfContent,
      subTitle,
      subPotTitle,
      header,
      headerPotential,
      selectedComponentIds,
      [importerGraphMapSectionExplanation, potentialGraphMapSectionDataExplanation]
    )
  }

  useImperativeHandle(ref, () => ({
    async getPdfContent () {
      return pdfDownload(true)
    }
  }))

  return (
    <>
      <Head title={t('menu.importer_companies')} />
      <Content>
        <McPageHeader title={t('menu.importer_companies')} />
        <MarketCalculusCard className="mb-2">
          <Nav tabs className="nav-tabs-s2">
            {[{ id: 1, label: 'spFirms' }, ...dynamicTabs.map((item, index) => ({ id: index + 3, label: item })), { id: 2, label: 'rfFirms' }].map(({ id, label }) => (
              <NavItem key={id}>
                <NavLink
                  href="#tab"
                  className={classnames('btn btn-sm', { 'btn-primary': activeTab === id })}
                  onClick={ev => {
                    ev.preventDefault()
                    toggleAltTab(id)
                  }}
                >
                  {t(`mailTemplate.wizard.firmList.tables.${label}`)}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </MarketCalculusCard>
        <TabContent activeTab={activeTab}>
          <TabPane tabId={1}><ImporterTableSection ref={importerTableRef} /><ImporterMapGraphSection /></TabPane>
          <TabPane tabId={2}><PotentialTableSection ref={potentialTableRef} /><PotentialMapGraphSection /></TabPane>
          {dynamicTabs.map((item, index) => {
            return (
              <TabPane key={index + 3} tabId={index + 3}>
                <TradeRegistryTabs id={item} dropdownData={tradeRegistry[item]}
                                   tableData={tradeRegistryTables[item] ?? []}
                                   setTableData={setTradeRegistryTables}
                                   selectedOptions={tradeRegistryDropdownSelection[item] ?? []}
                                   setSelectedOptions={setTradeRegistryDropdownSelection}/>
              </TabPane>
            )
          })}
        </TabContent>
        <FirmDetailsModal pageName={selectedTableName} />
      </Content>
    </>
  )
})

export default ImporterCompanies
